import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { YoutubeVideoPreviewData } from '../../../../../../../models/google-template/preview-interfaces/youtube-video-preview-data.interface';
import { UrlDisplayHelper } from '../../../../../../../utils/url-display-helper';
import { YoutubeFormatHelper } from '../../../../../../../utils/youtube-format-helper';
import { TvFrameComponent } from '../../../preview-common/tv-frame/tv-frame.component';

/**
 * Skippable in-stream video on the TV app. The creative fills the height of
 * the screen and sits centred, so a video that is not 16:9 is pillarboxed
 * rather than cropped.
 *
 * Unlike Demand Gen's TV watch ad there is no QR code beside the creative —
 * the YouTube Video Ad mock draws none — so the creative has the whole screen
 * to itself. The footer overlays the bottom of it, carrying the advertiser,
 * the destination and the two remote controls the viewer has.
 */
@Component({
    selector: 'app-youtube-video-in-stream-tv',
    imports: [CommonModule, TvFrameComponent],
    templateUrl: './youtube-in-stream-tv.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './youtube-in-stream-tv.component.scss'
})
export class YoutubeVideoInStreamTvPreviewComponent {
  @Input() data!: YoutubeVideoPreviewData;

  /** Thumbnail of the video, or '' when there isn't one yet. */
  get thumbnailUrl(): string {
    return YoutubeFormatHelper.thumbnailFor(this.videoId);
  }

  /**
   * True while the video is still being measured, so the empty player says so
   * rather than asking for a video that is about to turn up.
   */
  get isResolving(): boolean {
    return YoutubeFormatHelper.isResolving(this.videoId);
  }

  /** The footer gives the headline one line beside the buttons. */
  get headline(): string {
    return this.data.headline || 'Your headline appears here';
  }

  /** The full destination path, which is what the footer shows of the URL. */
  get displayUrl(): string {
    const url = (this.data.url ?? '').trim().replace(/^https?:\/\//i, '').replace(/\/$/, '');
    return url || UrlDisplayHelper.PLACEHOLDER_HOST;
  }

  private get videoId(): string {
    return this.data.video?.videoId ?? '';
  }
}
